import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FlatpickrDirective } from 'angularx-flatpickr';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [FormsModule, FlatpickrDirective],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  host: {
    '[attr.tabindex]': '-1',
  },
})
export class DatePicker {
  readonly calendarAppendTo = inject(DOCUMENT).body;

  @Input() placeholder = 'MM/DD/YYYY hh:mm A';

  // Internal flatpickr storage format — includes time now
  @Input() dateFormat = 'm/d/Y H:i';

  // What the user actually sees in the input (12hr with AM/PM)
  @Input() altFormat = 'm/d/Y h:i K';

  @Input() disabled = false;

  @Input() minDate: string | Date | null = null;

  @Input() maxDate: string | Date | null = null;

  @Input() model: string | Date | null = null;

  // New: toggle time picker on/off, and 24hr vs 12hr clock
  @Input() enableTime = true;

  @Input() time24hr = false;

  // Minute increment shown in the time dropdown
  @Input() minuteIncrement = 5;

  @Output() modelChange = new EventEmitter<string | Date | null>();

  private restrictedElements = new WeakSet<HTMLInputElement>();

  onChange(value: string | Date | null): void {
    this.model = value;
    this.modelChange.emit(value);
  }

  onDateSelected(event: { selectedDates: Date[]; dateString: string; instance: any }): void {
    event?.instance?.hourElement?.blur();
  }

  onReady(event: { instance: any }): void {
    const instance = event?.instance;
    instance?.calendarContainer?.classList.add('date-picker-calendar');
    this.restrictTimeInput(instance?.hourElement, 0, 12);   // ← flip min to 1 if you want strictly valid 12hr (01–12)
    this.restrictTimeInput(instance?.minuteElement, 0, 60); // ← flip max to 59 if you want strictly valid minutes
  }


  private restrictTimeInput(el: HTMLInputElement | undefined, min: number, max: number): void {
    if (!el || this.restrictedElements.has(el)) return;
    this.restrictedElements.add(el);

    el.setAttribute('maxlength', '2');

    el.addEventListener('input', () => {
      let raw = el.value.replace(/\D/g, '').slice(0, 2);
      el.value = raw;

      if (raw.length === 2) {
        const num = Number(raw);
        if (num < min) {
          el.value = String(min).padStart(2, '0');
        } else if (num > max) {
          el.value = String(max).padStart(2, '0');
        }
      }
    });

    el.addEventListener('blur', () => {
      if (el.value === '') return;
      const num = Number(el.value);
      const clamped = Math.min(Math.max(num, min), max);
      el.value = String(clamped).padStart(2, '0');
    });
  }
}